/**
 * Workspace Template Materializer (§183)
 *
 * Applies an out-of-band `workspace-template.json` artifact to an already-open
 * workspace session, materializing starter files, SQLite seed data, and
 * optionally media derivatives without writing scaffold files into workspace
 * history or reusing the auth `bootstrapWorkspace(...)` verb.
 *
 * Distinct from §180: §180 is the later cross-SDK bootstrap-manifest surface
 * with job registration and capability provisioning. This module stays focused
 * on already-open sessions plus template-driven files, databases, scaffolds,
 * and optional media-processing hooks.
 */

import type { WorkspaceSession } from './session'
import type { SqliteDatabase } from './sqliteDatabase'
import type { MediaVariant, MediaVariantName } from './types'
import { createComponentJobScaffold } from './componentJobs'
import type { ComponentJobStarterTemplate } from './componentJobs'

/**
 * Back-compat alias for callers that typed template materialization sessions
 * through this module instead of importing `WorkspaceSession` directly.
 */
export type WorkspaceTemplateSession = WorkspaceSession

// ── Template format ──────────────────────────────────────────────

/** Top-level metadata section */
export interface WorkspaceTemplateMeta {
  /** Human-readable template name */
  name: string
  /** Semantic version of the template format */
  version: string
  /** Optional description */
  description?: string
  /** Optional key-value tags */
  tags?: Record<string, string>
}

/** A file entry in the template */
export interface WorkspaceTemplateFile {
  /**
   * Absolute path within the workspace (must start with `/`).
   * Parent directories are created automatically.
   */
  path: string
  /**
   * File content. For binary files, use base64-encoded content and set
   * `encoding: 'base64'`.
   */
  content: string
  /**
   * Content encoding. Default: 'utf8'.
   * Use 'base64' for binary content.
   */
  encoding?: 'utf8' | 'base64'
  /**
   * Optional MIME type hint. Used only for documentation;
   * the workspace session does not interpret this field.
   */
  mimeType?: string
}

/** A database entry in the template */
export interface WorkspaceTemplateDatabase {
  /**
   * Absolute path within the workspace (must start with `/`).
   * The directory containing the DB must exist or be creatable.
   */
  path: string
  /**
   * SQL statements to run to create the schema.
   * Executed in order, each as a separate `exec()` call.
   * Should NOT include BEGIN/COMMIT — each statement is wrapped
   * in a transaction by the materializer.
   */
  schemaSql: string[]
  /**
   * Optional SQL statements to populate seed data.
   * Executed after schemaSql, each as a separate `exec()` call.
   * Run inside the same transaction as schemaSql.
   */
  seedSql?: string[]
}

/** A directory entry in the template — creates an empty directory at the given path. */
export interface WorkspaceTemplateDirectory {
  /**
   * Absolute path within the workspace (must start with `/`).
   * Parent directories are created automatically.
   */
  path: string
}

/** A media entry in the template — requests derived media variants for one source file. */
export interface WorkspaceTemplateMediaEntry {
  /**
   * Absolute path to the source media file within the workspace.
   * The file may come from `files`, `scaffolds`, or already-existing workspace content.
   */
  sourcePath: string
  /**
   * Specific variants to materialize. When omitted, the processor may generate
   * the default supported variant set for the source MIME type.
   */
  variants?: MediaVariantName[]
}

export interface WorkspaceTemplateMediaContext {
  session: WorkspaceTemplateSession
  signal?: AbortSignal
}

export type WorkspaceTemplateMediaProcessor = (
  entry: WorkspaceTemplateMediaEntry,
  context: WorkspaceTemplateMediaContext,
) => Promise<readonly MediaVariant[]>

/**
 * A component-job scaffold entry within a workspace template.
 *
 * When `applyWorkspaceTemplate` processes a scaffold entry, it:
 * 1. Calls `createComponentJobScaffold(jobRootPath, { template })` to produce
 *    the scaffold descriptor (files + directories).
 * 2. Materializes each scaffold file into the workspace at `/${stem}/${file.path}`,
 *    where `stem` is the jobRootPath with leading `/` and trailing `/` stripped.
 * 3. Records the jobRootPath in `result.appliedScaffolds`.
 *
 * This is the import direction of the §216 round-trip: templates can now
 * produce component-job directory structures in addition to files and databases.
 */
export interface WorkspaceTemplateScaffold {
  /**
   * Absolute path to the job root directory. Must end with `.job`.
   * The parent directory is created automatically if it does not exist.
   * Example: `/jobs/my-job.job/`
   */
  jobRootPath: string
  /**
   * Starter preset used to generate scaffold files.
   * Default: `'container-service'`
   */
  template?: 'minimal' | 'multi-file-service' | 'container-service'
  /**
   * When true, overwrite scaffold files that already exist with different content.
   * When false (the default), identical files are skipped (idempotent re-apply)
   * and files with different content produce an error.
   */
  overwrite?: boolean
}

/** The full workspace template artifact */
export interface WorkspaceTemplate {
  meta: WorkspaceTemplateMeta
  /** Ordered list of directory entries. Applied before files. */
  directories?: WorkspaceTemplateDirectory[]
  /** Ordered list of media entries. Processed after files, databases, and scaffolds. */
  media?: WorkspaceTemplateMediaEntry[]
  /** Ordered list of file entries. Processed in order. */
  files: WorkspaceTemplateFile[]
  /** Ordered list of database entries. */
  databases: WorkspaceTemplateDatabase[]
  /** Optional list of component-job scaffold entries. Processed after databases. */
  scaffolds?: WorkspaceTemplateScaffold[]
}

// ── Options ───────────────────────────────────────────────────────

export interface ApplyTemplateOptions {
  /**
   * Abort signal for database operations.
   * File operations are synchronous and cannot currently be aborted.
   */
  signal?: AbortSignal
  /**
   * When true (the default), skip files whose current content exactly matches
   * the template content (idempotent re-apply).
   */
  skipIdenticalFiles?: boolean
  /**
   * When true, emit a console.info line per applied file and database.
   * Default: false.
   */
  verbose?: boolean
  /**
   * Optional media processor that materializes `template.media` entries after
   * all files, databases, and scaffolds are in place.
   */
  mediaProcessor?: WorkspaceTemplateMediaProcessor
}

// ── Result ────────────────────────────────────────────────────────

export interface ApplyTemplateResult {
  /** Directories that were created. */
  appliedDirectories: string[]
  /** Files that were written (new or changed). */
  applied: string[]
  /** Files that were skipped because content already matched. */
  skipped: string[]
  /** Database paths where creation+schema+seed succeeded. */
  appliedDatabases: string[]
  /** Database paths skipped because the file already existed. */
  skippedDatabases: string[]
  /** Media source paths successfully processed. */
  appliedMedia: string[]
  /** Media-processing errors, separated from core file/database materialization errors. */
  mediaErrors: string[]
  /** Scaffold jobRootPaths that were fully materialized. */
  appliedScaffolds: string[]
  /** All errors encountered, with their context. */
  errors: string[]
}

// ── Validation Result ─────────────────────────────────────────────

export interface ValidationResult {
  valid: boolean
  errors: string[]
}

// ── Implementation helpers ────────────────────────────────────────

function isAbsolutePath(path: string): boolean {
  return path.startsWith('/')
}

function validateFileEntry(file: WorkspaceTemplateFile, errors: string[]): boolean {
  if (!isAbsolutePath(file.path)) {
    errors.push(`file: path must be absolute (got "${file.path}")`)
    return false
  }
  if (typeof file.content !== 'string') {
    errors.push(`file:${file.path}: content must be a string`)
    return false
  }
  if (file.encoding !== undefined && file.encoding !== 'utf8' && file.encoding !== 'base64') {
    errors.push(`file:${file.path}: encoding must be 'utf8' or 'base64' (got "${file.encoding}")`)
    return false
  }
  return true
}

function validateDatabaseEntry(db: WorkspaceTemplateDatabase, errors: string[]): boolean {
  if (!isAbsolutePath(db.path)) {
    errors.push(`database: path must be absolute (got "${db.path}")`)
    return false
  }
  if (!Array.isArray(db.schemaSql) || db.schemaSql.length === 0) {
    errors.push(`database:${db.path}: schemaSql must be a non-empty array`)
    return false
  }
  for (const sql of db.schemaSql) {
    if (typeof sql !== 'string') {
      errors.push(`database:${db.path}: each schemaSql entry must be a string`)
      return false
    }
  }
  if (db.seedSql !== undefined) {
    if (!Array.isArray(db.seedSql)) {
      errors.push(`database:${db.path}: seedSql must be an array`)
      return false
    }
    for (const sql of db.seedSql) {
      if (typeof sql !== 'string') {
        errors.push(`database:${db.path}: each seedSql entry must be a string`)
        return false
      }
    }
  }
  return true
}

function validateDirectoryEntry(dir: WorkspaceTemplateDirectory, errors: string[]): boolean {
  if (!isAbsolutePath(dir.path)) {
    errors.push(`directory: path must be absolute (got "${dir.path}")`)
    return false
  }
  return true
}

function validateMediaEntry(media: WorkspaceTemplateMediaEntry, errors: string[]): boolean {
  const sourcePath = typeof media.sourcePath === 'string' ? media.sourcePath : '<missing>'
  if (!isAbsolutePath(media.sourcePath)) {
    errors.push(`media: sourcePath must be absolute (got "${sourcePath}")`)
    return false
  }
  if (media.variants !== undefined) {
    if (!Array.isArray(media.variants)) {
      errors.push(`media:${media.sourcePath}: variants must be an array if present`)
      return false
    }
    for (const variant of media.variants) {
      if (typeof variant !== 'string' || variant.length === 0) {
        errors.push(`media:${media.sourcePath}: variants entries must be non-empty strings`)
        return false
      }
    }
  }
  return true
}

const VALID_SCAFFOLD_TEMPLATES = new Set(['minimal', 'multi-file-service', 'container-service'])

function validateScaffoldEntry(scaffold: WorkspaceTemplateScaffold, errors: string[]): boolean {
  if (!isAbsolutePath(scaffold.jobRootPath)) {
    errors.push(`scaffold: jobRootPath must be absolute (got "${scaffold.jobRootPath}")`)
    return false
  }
  // Normalize trailing slash before checking .job suffix.
  // Template paths may or may not include a trailing slash.
  const normalizedJobRootPath = scaffold.jobRootPath.replace(/\/$/, '')
  if (!normalizedJobRootPath.endsWith('.job')) {
    errors.push(`scaffold: jobRootPath must end with .job (got "${scaffold.jobRootPath}")`)
    return false
  }
  if (scaffold.template !== undefined && !VALID_SCAFFOLD_TEMPLATES.has(scaffold.template)) {
    errors.push(`scaffold: template must be one of ${[...VALID_SCAFFOLD_TEMPLATES].join(', ')} (got "${scaffold.template}")`)
    return false
  }
  return true
}

function checkDuplicateFilePaths(files: WorkspaceTemplateFile[], errors: string[]): void {
  const seen = new Map<string, number>()
  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    const prior = seen.get(file.path)
    if (prior !== undefined) {
      errors.push(`file: duplicate path "${file.path}" (first seen at index ${prior}, also at index ${i})`)
    } else {
      seen.set(file.path, i)
    }
  }
}

function doValidateTemplate(template: unknown, errors: string[]): template is WorkspaceTemplate {
  if (!template || typeof template !== 'object') {
    errors.push('template must be a non-null object')
    return false
  }
  const t = template as Record<string, unknown>
  if (!t.meta || typeof t.meta !== 'object') {
    errors.push('template.meta is required and must be an object')
    return false
  }
  const meta = t.meta as Record<string, unknown>
  if (typeof meta.name !== 'string' || !meta.name) {
    errors.push('template.meta.name is required and must be a non-empty string')
    return false
  }
  if (typeof meta.version !== 'string' || !meta.version) {
    errors.push('template.meta.version is required and must be a non-empty string')
    return false
  }
  if (!Array.isArray(t.files)) {
    errors.push('template.files must be an array')
    return false
  }
  if (!Array.isArray(t.databases)) {
    errors.push('template.databases must be an array')
    return false
  }
  if (t.directories !== undefined && !Array.isArray(t.directories)) {
    errors.push('template.directories must be an array if present')
    return false
  }
  if (t.media !== undefined && !Array.isArray(t.media)) {
    errors.push('template.media must be an array if present')
    return false
  }
  for (const file of t.files as unknown[]) {
    if (!file || typeof file !== 'object') {
      errors.push('template.files entries must be objects')
      return false
    }
    validateFileEntry(file as WorkspaceTemplateFile, errors)
  }
  checkDuplicateFilePaths(t.files as WorkspaceTemplateFile[], errors)
  for (const db of t.databases as unknown[]) {
    if (!db || typeof db !== 'object') {
      errors.push('template.databases entries must be objects')
      return false
    }
    validateDatabaseEntry(db as WorkspaceTemplateDatabase, errors)
  }
  for (const dir of (t.directories ?? []) as unknown[]) {
    if (!dir || typeof dir !== 'object') {
      errors.push('template.directories entries must be objects')
      return false
    }
    validateDirectoryEntry(dir as WorkspaceTemplateDirectory, errors)
  }
  for (const media of (t.media ?? []) as unknown[]) {
    if (!media || typeof media !== 'object') {
      errors.push('template.media entries must be objects')
      return false
    }
    validateMediaEntry(media as WorkspaceTemplateMediaEntry, errors)
  }
  if (t.scaffolds !== undefined) {
    if (!Array.isArray(t.scaffolds)) {
      errors.push('template.scaffolds must be an array if present')
    } else {
      for (const scaffold of t.scaffolds) {
        if (!scaffold || typeof scaffold !== 'object') {
          errors.push('template.scaffolds entries must be objects')
        } else {
          validateScaffoldEntry(scaffold as WorkspaceTemplateScaffold, errors)
        }
      }
    }
  }
  return errors.length === 0
}

// ── Standalone Validation API (§285) ─────────────────────────────

/**
 * Validate a workspace template object for structural integrity — without
 * applying it or producing any side effects.
 *
 * Use this before `applyWorkspaceTemplate` to fail fast with clear diagnostics
 * when a template has missing fields, duplicate paths, or invalid entries.
 *
 * @param template Any value — returned as invalid if not a properly-shaped object.
 * @returns A `ValidationResult` with `valid: true` and an empty `errors` array on success,
 *          or `valid: false` and a list of diagnostic strings on failure.
 *
 * @example
 * ```ts
 * const result = validateWorkspaceTemplate(someTemplate)
 * if (!result.valid) {
 *   console.error('Template errors:', result.errors)
 *   return
 * }
 * // safe to apply
 * await applyWorkspaceTemplate(session, someTemplate)
 * ```
 */
export function validateWorkspaceTemplate(template: unknown): ValidationResult {
  const errors: string[] = []
  const valid = doValidateTemplate(template, errors)
  return { valid, errors }
}

// ── Internal apply path ─────────────────────────────────────────────

function decodeBase64ToBytes(b64: string): Uint8Array | null {
  try {
    const binary = atob(b64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes
  } catch {
    return null
  }
}

// ── Public API ────────────────────────────────────────────────────

/**
 * Apply a workspace template to an already-open session.
 *
 * Files are written via `session.writeText()` (UTF-8) or `session.writeBytes()`
 * (base64-decoded). When `options.skipIdenticalFiles` is true (the default),
 * files whose current content exactly matches the template content are skipped.
 *
 * Database entries are create-if-missing: when the DB path already exists it
 * is skipped (v0 does not attempt schema diff or migration). When the path
 * is absent, the DB is created, schemaSql is executed, then seedSql is executed,
 * all inside one transaction per database.
 *
 * Directories are created before files so that parent paths exist for file writes.
 *
 * @param session An already-open workspace session (e.g. from `sdk.openWorkspace(...)`).
 * @param template A parsed workspace-template.json object. May contain optional `directories`.
 * @param options Optional apply options.
 * @returns An `ApplyTemplateResult` with `appliedDirectories`, `applied`, `skipped`, `appliedDatabases`, `skippedDatabases`, and `errors`.
 *
 * @example
 * ```ts
 * const ws = await sdk.openWorkspace('my-workspace')
 * const template: WorkspaceTemplate = {
 *   meta: { name: 'my-template', version: '1.0.0' },
 *   files: [{ path: '/README.md', content: '# Hello' }],
 *   databases: [{
 *     path: '/data.db',
 *     schemaSql: ['CREATE TABLE t (id INTEGER PRIMARY KEY)'],
 *     seedSql: ['INSERT INTO t VALUES (1)'],
 *   }],
 * }
 * const result = await applyWorkspaceTemplate(ws, template)
 * console.log(result.applied, result.errors)
 * ```
 */
export async function applyWorkspaceTemplate(
  session: WorkspaceSession,
  template: WorkspaceTemplate,
  options: ApplyTemplateOptions = {},
): Promise<ApplyTemplateResult> {
  const {
    signal,
    skipIdenticalFiles = true,
    verbose = false,
    mediaProcessor,
  } = options

  const result: ApplyTemplateResult = {
    appliedDirectories: [],
    applied: [],
    skipped: [],
    appliedDatabases: [],
    skippedDatabases: [],
    appliedMedia: [],
    mediaErrors: [],
    appliedScaffolds: [],
    errors: [],
  }

  // Validate template structure before touching anything.
  if (!doValidateTemplate(template, result.errors)) {
    // doValidateTemplate populates errors; return early on structural failure.
    return result
  }

  // ── Directories ─────────────────────────────────────────────────
  // Create directories before files so that parent paths exist.
  // session.mkdir creates parent directories automatically.
  for (const dir of template.directories ?? []) {
    try {
      session.mkdir(dir.path)
      result.appliedDirectories.push(dir.path)
      if (verbose) console.info(`[applyWorkspaceTemplate] created directory: ${dir.path}`)
    } catch (err) {
      result.errors.push(`directory:${dir.path}: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  // ── Files ──────────────────────────────────────────────────────
  for (const file of template.files) {
    const encoding = file.encoding ?? 'utf8'

    // Idempotent re-apply: skip if content already matches.
    if (skipIdenticalFiles && encoding === 'utf8') {
      try {
        const existing = await session.readText(file.path)
        if (existing === file.content) {
          result.skipped.push(file.path)
          if (verbose) console.info(`[applyWorkspaceTemplate] skipped (identical): ${file.path}`)
          continue
        }
      } catch {
        // File does not exist — fall through to write.
      }
    }

    try {
      if (encoding === 'utf8') {
        session.writeText(file.path, file.content)
      } else {
        // base64 → bytes
        const bytes = decodeBase64ToBytes(file.content)
        if (!bytes) {
          result.errors.push(`file:${file.path}: failed to base64-decode content`)
          continue
        }
        session.writeBytes(file.path, bytes)
      }
      result.applied.push(file.path)
      if (verbose) console.info(`[applyWorkspaceTemplate] applied: ${file.path}`)
    } catch (err) {
      result.errors.push(`file:${file.path}: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  // ── Databases ──────────────────────────────────────────────────
  for (const dbEntry of template.databases) {
    if (signal?.aborted) {
      result.errors.push(`database:${dbEntry.path}: aborted before open`)
      break
    }

    // Check whether the DB already exists.
    let db: SqliteDatabase | null = null
    let dbExisted = false
    try {
      db = await session.openSqlite(dbEntry.path, { signal })
      dbExisted = true
    } catch {
      // Path does not exist — will be created below.
      dbExisted = false
    }

    if (dbExisted && db) {
      // create-if-missing: skip existing DB paths in v0.
      result.skippedDatabases.push(dbEntry.path)
      if (verbose) console.info(`[applyWorkspaceTemplate] skipped (exists): ${dbEntry.path}`)
      session.closeSqliteDb(db)
      continue
    }

    // Open (create) + populate the database.
    try {
      db = await session.openSqlite(dbEntry.path, { signal })
      if (signal?.aborted) {
        result.errors.push(`database:${dbEntry.path}: aborted during open`)
        if (db) session.closeSqliteDb(db)
        break
      }

      const conn = await db.connect()
      try {
        // Wrap schema + seed in a single transaction per database.
        await conn.transaction(async (tx) => {
          for (const sql of dbEntry.schemaSql) {
            await tx.exec(sql)
          }
          if (dbEntry.seedSql) {
            for (const sql of dbEntry.seedSql) {
              await tx.exec(sql)
            }
          }
        })
      } finally {
        await conn.close()
      }

      result.appliedDatabases.push(dbEntry.path)
      if (verbose) console.info(`[applyWorkspaceTemplate] applied database: ${dbEntry.path}`)
    } catch (err) {
      result.errors.push(
        `database:${dbEntry.path}: ${err instanceof Error ? err.message : String(err)}`,
      )
    } finally {
      if (db) session.closeSqliteDb(db)
    }
  }

  // ── Scaffolds ──────────────────────────────────────────────────
  // Process component-job scaffold entries after files and databases.
  // Each entry produces a scaffold (files + directories) via createComponentJobScaffold
  // and materializes all files into the workspace at /{stem}/{file.path}.
  for (const scaffoldEntry of template.scaffolds ?? []) {
    const { jobRootPath, template: scaffoldTemplate, overwrite = false } = scaffoldEntry

    // Normalize: strip trailing slash so path is consistent with createComponentJobScaffold's requirement.
    const normalizedJobRootPath = jobRootPath.replace(/\/$/, '')

    // Derive the path stem for materialization: '/jobs/my-job.job/' -> 'jobs/my-job.job'
    const stem = normalizedJobRootPath.replace(/^\//, '')
    const templateValue: ComponentJobStarterTemplate = (scaffoldTemplate ?? 'container-service') as ComponentJobStarterTemplate

    let scaffold: ReturnType<typeof createComponentJobScaffold>
    try {
      scaffold = createComponentJobScaffold(normalizedJobRootPath + '/', { template: templateValue })
    } catch (err) {
      result.errors.push(`scaffold:${jobRootPath}: ${err instanceof Error ? err.message : String(err)}`)
      continue
    }

    // Track scaffold-level errors: only add to appliedScaffolds when no errors occurred.
    let scaffoldHadError = false

    // Create scaffold directories first.
    for (const dir of scaffold.directories) {
      const dirPath = `/${stem}/${dir}/`
      try {
        session.mkdir(dirPath)
        if (verbose) console.info(`[applyWorkspaceTemplate] created scaffold directory: ${dirPath}`)
      } catch (err) {
        result.errors.push(`scaffold directory:${dirPath}: ${err instanceof Error ? err.message : String(err)}`)
        scaffoldHadError = true
      }
    }

    // Materialize scaffold files.
    for (const file of scaffold.files) {
      const filePath = `/${stem}/${file.path}`

      // Check for content collision with explicit template files.
      const explicitFile = template.files.some(f => f.path === filePath)
      if (explicitFile) {
        result.errors.push(`scaffold:${jobRootPath}: file path collides with explicit template file: ${filePath}`)
        scaffoldHadError = true
        continue
      }

      // Idempotent skip: identical content is skipped when overwrite is false.
      if (!overwrite && skipIdenticalFiles) {
        try {
          const existing = await session.readText(filePath)
          if (existing === file.content) {
            if (verbose) console.info(`[applyWorkspaceTemplate] skipped scaffold file (identical): ${filePath}`)
            continue
          }
          // Content differs and overwrite is false — error.
          result.errors.push(`scaffold:${jobRootPath}: file has different content and overwrite=false: ${filePath}`)
          scaffoldHadError = true
          continue
        } catch {
          // File does not exist — fall through to write.
        }
      } else if (!overwrite) {
        // overwrite is false but skipIdenticalFiles is also false — always write,
        // but still check for the collision-with-different-content case.
        try {
          const existing = await session.readText(filePath)
          if (existing !== file.content) {
            result.errors.push(`scaffold:${jobRootPath}: file has different content and overwrite=false: ${filePath}`)
            scaffoldHadError = true
            continue
          }
        } catch {
          // File does not exist — fall through to write.
        }
      }

      try {
        session.writeText(filePath, file.content)
        result.applied.push(filePath)
        if (verbose) console.info(`[applyWorkspaceTemplate] applied scaffold file: ${filePath}`)
      } catch (err) {
        result.errors.push(`scaffold file:${filePath}: ${err instanceof Error ? err.message : String(err)}`)
        scaffoldHadError = true
      }
    }

    if (!scaffoldHadError) {
      result.appliedScaffolds.push(jobRootPath)
      if (verbose) console.info(`[applyWorkspaceTemplate] applied scaffold: ${jobRootPath}`)
    }
  }

  // ── Media ──────────────────────────────────────────────────────
  // Process media entries last so they can target files produced by explicit
  // template files, databases, or scaffold-generated starter assets.
  for (const mediaEntry of template.media ?? []) {
    if (signal?.aborted) {
      result.mediaErrors.push(`media:${mediaEntry.sourcePath}: aborted before process`)
      break
    }

    if (!mediaProcessor) {
      result.mediaErrors.push(`media:${mediaEntry.sourcePath}: no mediaProcessor configured`)
      continue
    }

    try {
      await mediaProcessor(mediaEntry, { session, signal })
      result.appliedMedia.push(mediaEntry.sourcePath)
      if (verbose) console.info(`[applyWorkspaceTemplate] applied media: ${mediaEntry.sourcePath}`)
    } catch (err) {
      result.mediaErrors.push(`media:${mediaEntry.sourcePath}: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  return result
}
